import { useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../ReportTable.component";
import ReportService from "../../../services/reports/Report.service";
import CustomReportService from "../../../services/reports/CustomReport.service";
import EpochMonthService from "../../../services/EpochMonth.service";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import * as XLSX from "xlsx";
import MultiSelect from "../../../tailwindComponents/ui/multi-select";

interface MKECustomReportProps {
  clientId: string | number;
  epochMonth: number | null;
}

// Raw record (fields returned by GetMKECustomReportData.sql)
export interface MKECustomRaw {
    locationCode: string;
    locationName: string;
    classCode: string;
    className: string;
    category: string | null;
    salesYTD: number | null;
    salesYTDLastYear: number | null;
    salesPlan: number | null;
    salesPlanLm: number | null;
    inventoryBomPlanAvg: number | null;
    salesLm: number | null;
    salesSumLm: number | null;
    markdownsSumLm: number | null;
    inflowCostSumLm: number | null;
    inflowRetailSumLm: number | null;
    inventoryBomAvgLm: number | null;
    inventoryBomAvgCostLm: number | null;
    inventoryBomSpan: number | null;
    inventoryEomSpan: number | null;
    inventoryDeviationRm: number | null;
    inventoryDeviationCostRm: number | null;
    otbCostEom: number | null;
    otbCostEom90: number | null;
    salesLmLy?: number | null;
    inventoryBomLy?: number | null;
    salesRm?: number | null;
    inventoryCurrent?: number | null;
    inventoryBomPlanRm?: number | null;
    inventoryBomRm?: number | null;
    salesAnnualPlan?: number | null;
}

// Parsed type for hierarchy grouping
interface MKEParsedData extends BaseReportParsedData {
    salesYTD: MoneyDataItem;
    salesYTDLastYear: MoneyDataItem;
    salesPlan: MoneyDataItem;
    salesPlanLm: MoneyDataItem;
    inventoryBomPlanAvg: MoneyDataItem;
    salesLm: MoneyDataItem;
    salesSumLm: MoneyDataItem;
    markdownsSumLm: MoneyDataItem;
    inflowCostSumLm: MoneyDataItem;
    inflowRetailSumLm: MoneyDataItem;
    inventoryBomAvgLm: MoneyDataItem;
    inventoryBomAvgCostLm: MoneyDataItem;
    inventoryBomSpan: MoneyDataItem;
    inventoryEomSpan: MoneyDataItem;
    inventoryDeviationRm: MoneyDataItem;
    inventoryDeviationCostRm: MoneyDataItem;
    otbCostEom: MoneyDataItem;
    otbCostEom90: MoneyDataItem;
    // optional
    salesLmLy: MoneyDataItem;
    inventoryBomLy: MoneyDataItem;
    salesRm: MoneyDataItem;
    inventoryCurrent: MoneyDataItem;
    inventoryBomPlanRm: MoneyDataItem;
    inventoryBomRm: MoneyDataItem;
    salesAnnualPlan: MoneyDataItem;
}

const HIERARCHY_OPTIONS = {
  "By Location": ["locationCode"] as const,
  "By Category": ["category"] as const,
  "Class by Location": ["classCode", "locationCode"] as const,
};

function num(v: any): number { return (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v); }

function parseRaw(rows: MKECustomRaw[]): MKEParsedData[] {
  return (rows || []).map(r => ({
    locationCode: r.locationCode,
    locationName: r.locationName,
    classCode: r.classCode,
    className: r.className,
    category: r.category ?? "N/A",
    parentClientClassCode: null,

    salesYTD: new MoneyDataItem(num(r.salesYTD)),
    salesYTDLastYear: new MoneyDataItem(num(r.salesYTDLastYear)),
    salesPlan: new MoneyDataItem(num(r.salesPlan)),
    salesPlanLm: new MoneyDataItem(num(r.salesPlanLm)),
    inventoryBomPlanAvg: new MoneyDataItem(num(r.inventoryBomPlanAvg)),
    salesLm: new MoneyDataItem(num(r.salesLm)),
    salesSumLm: new MoneyDataItem(num(r.salesSumLm)),
    markdownsSumLm: new MoneyDataItem(num(r.markdownsSumLm)),
    inflowCostSumLm: new MoneyDataItem(num(r.inflowCostSumLm)),
    inflowRetailSumLm: new MoneyDataItem(num(r.inflowRetailSumLm)),
    inventoryBomAvgLm: new MoneyDataItem(num(r.inventoryBomAvgLm)),
    inventoryBomAvgCostLm: new MoneyDataItem(num(r.inventoryBomAvgCostLm)),
    inventoryBomSpan: new MoneyDataItem(num(r.inventoryBomSpan)),
    inventoryEomSpan: new MoneyDataItem(num(r.inventoryEomSpan)),
    inventoryDeviationRm: new MoneyDataItem(num(r.inventoryDeviationRm)),
    inventoryDeviationCostRm: new MoneyDataItem(num(r.inventoryDeviationCostRm)),
    otbCostEom: new MoneyDataItem(num(r.otbCostEom)),
    otbCostEom90: new MoneyDataItem(num(r.otbCostEom90)),

    // optional
    salesLmLy: new MoneyDataItem(num(r.salesLmLy)),
    inventoryBomLy: new MoneyDataItem(num(r.inventoryBomLy)),
    salesRm: new MoneyDataItem(num(r.salesRm)),
    inventoryCurrent: new MoneyDataItem(num(r.inventoryCurrent)),
    inventoryBomPlanRm: new MoneyDataItem(num(r.inventoryBomPlanRm)),
    inventoryBomRm: new MoneyDataItem(num(r.inventoryBomRm)),

    // derived or alias
    salesAnnualPlan: new MoneyDataItem(num(r.salesAnnualPlan ?? r.salesPlan)),
  }));
}

const MKECustomReport = ({ clientId, epochMonth }: MKECustomReportProps) => {
  const raw = useRef<MKECustomRaw[]>([]);
  const [parsed, setParsed] = useState<MKEParsedData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [locationFilter, setLocationFilter] = useState<string[]>(["ALL"]);
  const [hierarchyKey, setHierarchyKey] = useState<keyof typeof HIERARCHY_OPTIONS>("By Location");
  const hierarchy = HIERARCHY_OPTIONS[hierarchyKey] as readonly string[];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await CustomReportService.getMKECustomReportData({ clientId, epochMonth: epochMonth ?? EpochMonthService.getTodayEpochMonth() });
        raw.current = (data as MKECustomRaw[]) || [];
        const _parsed = parseRaw(raw.current);
        setParsed(_parsed);
      } catch (e) {
        setParsed([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, epochMonth]);

  const locations = useMemo(() => {
    const set = new Map<string, string>();
    parsed.forEach(p => { if (p.locationCode) set.set(p.locationCode, `${p.locationCode} ${p.locationName}`); });
    return [{ code: "ALL", label: "All Locations" }, ...Array.from(set.entries()).map(([code, label]) => ({ code, label }))];
  }, [parsed]);

  const filtered = useMemo(() => {
    const locArr = (Array.isArray(locationFilter) && locationFilter.includes("ALL")) ? [] : (locationFilter || []);
    return ReportService.sortAndFilterData(parsed, [...hierarchy], locArr, [], []);
  }, [parsed, locationFilter, hierarchy]);

  const columns = useMemo(() => {
        const hierarchyColumns = hierarchy.map(h => ({ name: h === 'locationCode' ? 'Location' : h === 'category' ? 'Category' : h === 'classCode' ? 'Class' : '', sortable: false }));
        return [
            ...hierarchyColumns,
            { name: "Annual Rate", sortable: false },
            { name: "MMU", sortable: false },
            { name: "Turn", sortable: false },
            { name: "GMROI", sortable: false },
            { name: "Profit $$", sortable: false },
                { name: "Sales Rank $$", sortable: false },
            { name: "Sales % YTD", sortable: false },
            { name: "LY Sales YTD", sortable: false },
            { name: "Sales YTD Diff", sortable: false },
            { name: "Updated Sales", sortable: false },
            { name: "LY MO. Sales", sortable: false },
            { name: "TY MO. Sales", sortable: false },
            { name: "Sales Diff Ret", sortable: false },
            { name: "Plan MO. Sales Ret", sortable: false },
            { name: "Sales to Plan RET", sortable: false },
            { name: "Plan FOM Ret. Inv", sortable: false },
            { name: "TY ACT. FOM RET. INV", sortable: false },
            { name: "Update Inv", sortable: false },
            { name: "RET. Inv Diff", sortable: false },
            { name: "LY FOM RET. Inv", sortable: false },
            { name: "Cost 90 Day OTB", sortable: false },
        ];
  }, [hierarchy]);

  const tableData = useMemo(() => {
    return filtered.map(r => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...hierarchy]);

      const salesPlanAnnual = num(r.salesAnnualPlan?.value);
      const salesSumLm = num(r.salesSumLm?.value); // TY month sales
      const markdownsSumLm = num(r.markdownsSumLm?.value);
      const inflowRetail = num(r.inflowRetailSumLm?.value);
      const inflowCost = num(r.inflowCostSumLm?.value);
      const invAvgLm = num(r.inventoryBomAvgLm?.value);
      const invAvgCostLm = num(r.inventoryBomAvgCostLm?.value);
      const salesYTD = num(r.salesYTD?.value);
      const salesYTDLastYear = num(r.salesYTDLastYear?.value);
      const salesPlanLm = num(r.salesPlanLm?.value);
      const invPlanFom = num(r.inventoryBomPlanRm?.value); // plan FOM
      const invBomActual = num(r.inventoryBomRm?.value); // actual BOM
      const invCurrent = num(r.inventoryCurrent?.value);
      const invLyFom = num(r.inventoryBomLy?.value);
      const salesLmLy = num(r.salesLmLy?.value);
      const inventoryDeviationCostRm = num(r.inventoryDeviationCostRm?.value);
      const otbCostEom90 = num(r.otbCostEom90?.value);
      const salesLm = num(r.salesLm?.value);

      // IMU & MMU
      const imu = inflowRetail > 0 ? (inflowRetail - inflowCost) / inflowRetail : 0;
      const costComplement = 1 - imu;
      const markdownPercent = salesSumLm > 0 ? (markdownsSumLm / salesSumLm) : 0;
      const mmu = imu - (markdownPercent * costComplement);

      // Turn
      const turn = invAvgLm > 0 ? (salesSumLm / invAvgLm) : 0;

      // GMROI
      const gmroi = invAvgCostLm > 0 ? ((salesSumLm * mmu) / invAvgCostLm) : 0;

      // Profit $$
      const profit = salesSumLm * mmu;

      // Sales % YTD
      const salesPctYtd = salesYTD > 0 ? ((salesYTD - salesYTDLastYear) / salesYTD) * 100 : 0;

      // Sales YTD Diff
      const salesYtdDiff = salesYTD - salesYTDLastYear;

      // Updated Sales strictly salesRm
      const updatedSales = num(r.salesRm?.value);

      // Sales Diff Ret (salesLm - salesLmLy) using salesSumLm as current retail month sales
      const salesDiffRet = salesLm - salesLmLy;

      // Sales to Plan RET (salesLm - salesPlanLm) using salesSumLm as month sales
      const salesToPlanRet = salesLm - salesPlanLm;

      // RET Inv Diff (inventorybomrm - inventorybomplanrm)
      const retInvDiff = invBomActual - invPlanFom;

      // Cost 90 Day OTB (otbCostEom90 + inventoryDeviationCostRm)
      const cost90DayOtb = otbCostEom90 + inventoryDeviationCostRm;

      return [
        ...labels,
        ReportService.formatCurrency(salesPlanAnnual), // Annual Rate
        ReportService.formatPercent(mmu * 100), // MMU
        (Math.round(turn * 10) / 10).toFixed(1), // Turn
        (Math.round(gmroi * 10) / 10).toFixed(1), // GMROI
        ReportService.formatCurrency(profit), // Profit $$
        ReportService.formatCurrency(salesSumLm), // Sales Rank $$
        ReportService.formatPercent(salesPctYtd), // Sales % YTD
        ReportService.formatCurrency(salesYTDLastYear), // LY Sales YTD
        ReportService.formatCurrency(salesYtdDiff), // Sales YTD Diff
        ReportService.formatCurrency(updatedSales), // Updated Sales
        ReportService.formatCurrency(salesLmLy), // LY MO. Sales
        ReportService.formatCurrency(num(r.salesLm?.value)), // TY MO. Sales
        ReportService.formatCurrency(salesDiffRet), // Sales Diff Ret
        ReportService.formatCurrency(salesPlanLm), // Plan MO. Sales Ret
        ReportService.formatCurrency(salesToPlanRet), // Sales to Plan RET
        ReportService.formatCurrency(invPlanFom), // Plan FOM Ret. Inv
        ReportService.formatCurrency(invBomActual), // TY ACT. FOM RET. INV
        ReportService.formatCurrency(invCurrent), // Update Inv
        ReportService.formatCurrency(retInvDiff), // RET. Inv Diff
        ReportService.formatCurrency(invLyFom), // LY FOM RET. Inv
        ReportService.formatCurrency(cost90DayOtb), // Cost 90 Day OTB
      ];
    });
  }, [filtered, hierarchy]);

  const rowClassName = (rowIndex: number) => {
    if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
    return ReportService.getRowClassName(tableData[rowIndex], [...hierarchy]);
  };

  const exportToExcel = () => {
    try {
      const header = columns.map(c => c.name);
      const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MKECustom');
      const fileName = `MKE_Custom_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-[1700px]">
      <h2 className="text-2xl font-bold mb-2">
        {`${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())} MKE Custom (${hierarchyKey})`}
      </h2>
      <div className="w-full flex flex-wrap items-center justify-end pr-4 mb-2 gap-2">
        <div className="flex items-center gap-2 mr-auto pl-2">
          <label className="text-sm">Location:</label>
          <MultiSelect
            options={locations.map(l => ({ code: l.code, label: l.label }))}
            value={locationFilter}
            onChange={(v) => setLocationFilter(v)}
            placeholder="All Locations"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Hierarchy:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={hierarchyKey}
            onChange={(e) => setHierarchyKey(e.target.value as keyof typeof HIERARCHY_OPTIONS)}
          >
            {Object.keys(HIERARCHY_OPTIONS).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <button
          onClick={exportToExcel}
          className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
        >
          Download Excel
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="w-full h-full">
          <ReportTable
            columns={columns}
            data={tableData}
            rowClassName={rowClassName}
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
            stickyHeader={true}
            hierarchyDepth={hierarchy ? hierarchy.length : 0}
          />
        </div>
      )}
    </div>
  );
};

export default MKECustomReport;
